/**
 * <localization-search> — client-side filter for the footer market (country) list.
 *
 * Wraps a search input ([data-localization-search-input]) and a list of
 * .footer-localization__item rows. On input it hides rows whose text doesn't
 * contain the query (case-insensitive substring, like the reference applyFilter).
 * No dependencies; filtering is purely visual (the native localization form still
 * submits on option click).
 */
class LocalizationSearch extends HTMLElement {
  #input;
  #items;
  #highlighted;
  #noResults;
  #onInput;
  #onKeydown;
  #onDrawerClosed;
  #initialized = false;

  connectedCallback() {
    if (this.#initialized) return;

    this.#input = this.querySelector('[data-localization-search-input]');
    // Only the full list is searchable — the highlighted group repeats markets that are
    // already in it, so filtering both would show a match twice (869eavgn9).
    const allMarkets = this.querySelector('[data-localization-all-markets]') ?? this;
    this.#items = Array.from(allMarkets.querySelectorAll('.footer-localization__item'));
    this.#highlighted = this.querySelector('[data-localization-highlighted]');
    if (!this.#input || !this.#items.length) return;

    this.#initialized = true;
    this.#noResults = this.querySelector('.footer-localization__no-results');

    this.#onInput = () => this.#filter();
    this.#input.addEventListener('input', this.#onInput);

    // Escape is a two-step: 1st clears a non-empty filter (keeping the drawer
    // open); when the input is already empty, let it bubble to the drawer's
    // document-level handler so Escape still closes the drawer.
    this.#onKeydown = (event) => {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      if (!this.#input.value) return;
      event.stopPropagation();
      this.#reset();
    };
    this.#input.addEventListener('keydown', this.#onKeydown);

    // The drawer only hides (display:none) on close — it never unmounts us, so
    // disconnectedCallback won't fire. Reset the filter when our drawer closes
    // so a stale query doesn't leak into the next open.
    this.#onDrawerClosed = (event) => {
      if (event.target === this.closest('drawer-component')) this.#reset();
    };
    document.addEventListener('drawer:closed', this.#onDrawerClosed);
  }

  disconnectedCallback() {
    this.#input?.removeEventListener('input', this.#onInput);
    this.#input?.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('drawer:closed', this.#onDrawerClosed);
    this.#initialized = false;
  }

  #reset() {
    if (!this.#input) return;
    this.#input.value = '';
    this.#filter();
  }

  #filter() {
    const query = this.#input.value.trim().toLowerCase();
    let visible = 0;

    this.#items.forEach((item) => {
      const text = item.textContent.trim().toLowerCase();
      const match = !query || text.includes(query);
      item.hidden = !match;
      if (match) visible += 1;
    });

    if (this.#highlighted) this.#highlighted.hidden = Boolean(query);
    if (this.#noResults) this.#noResults.hidden = visible !== 0;
  }
}

if (!customElements.get('localization-search')) {
  customElements.define('localization-search', LocalizationSearch);
}
